import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Button, Alert, Spinner, ListGroup, Badge } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { prepJpeg } from '../utils/imagePrep';

// Public community photo contribution (#94 / #97) for the customer web app (#99).
// Mirrors the iOS unified "Add a photo" sheet: pick a target (the restaurant, an
// existing menu item, or a brand-new dish matched against the shared Food
// catalog), attach a photo, optional caption, submit for admin moderation.
//
// New-dish modelling (#103): the dish NAME and the catalog-Food MATCH are two
// independent things. The name is what the contributor calls the dish (always
// sent, becomes the menu item's custom_name); the match only categorizes it
// ("what kind of thing is this") and never decides WHICH menu item this is.
// Coming through "Another dish…" always CREATES a new dish. If the matched food
// is one the restaurant already serves, we show a non-blocking nudge — attaching
// to an existing item is done via the "Photo of" list at the top, which already
// lists every menu item (so we never present an ambiguous "is it this one?" that
// could be misread as categorizing).
//
// Upload path is identical to the private visit-photo flow (EditVisitModal):
// EXIF-strip re-encode → presign → PUT straight to R2 → record. The PUT goes
// browser→R2, so the public bucket needs a CORS policy allowing PUT from this
// origin (a Cloudflare/R2 config step, separate from this code).
function ContributePhotoModal({ show, onHide, restaurant, menuItems = [] }) {
  const { token } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Target: 'restaurant' | 'mi:<menuItemId>' | 'new'
  const [targetValue, setTargetValue] = useState('restaurant');

  // New-dish: the dish's own name + an optional link to a known catalog Food.
  const [dishName, setDishName] = useState('');
  const [matchedFood, setMatchedFood] = useState(null); // { id, name } category, if picked
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [caption, setCaption] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const resetAll = () => {
    setTargetValue('restaurant');
    setDishName('');
    setMatchedFood(null);
    setSuggestions([]);
    setFile(null);
    setPreviewUrl('');
    setCaption('');
    setBusy(false);
    setError('');
    setSuccess(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Reset whenever the modal is (re)opened so a fresh submission starts clean.
  useEffect(() => {
    if (show) resetAll();
  }, [show]);

  // Revoke object URLs to avoid leaks when the preview changes/unmounts.
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  // Debounced catalog typeahead — only while proposing a NEW dish and the user
  // hasn't already locked in a catalog match.
  useEffect(() => {
    if (targetValue !== 'new' || matchedFood) { setSuggestions([]); return; }
    const term = dishName.trim();
    if (term.length < 2) { setSuggestions([]); return; }
    let cancelled = false;
    setSuggestLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/foods/search/${encodeURIComponent(term)}`);
        const data = res.ok ? await res.json() : [];
        if (!cancelled) setSuggestions(Array.isArray(data) ? data.slice(0, 6) : []);
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setSuggestLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [dishName, targetValue, matchedFood, API_BASE_URL]);

  // Existing menu items at this restaurant that share the matched catalog food.
  // Used only for a non-blocking heads-up (attach is via the "Photo of" list).
  const foodId = (mi) => mi.food?.id ?? mi.food_id;
  const miLabel = (mi) => mi.custom_name || mi.food?.name || `Menu item #${mi.id}`;
  const existingForFood = matchedFood
    ? menuItems.filter((mi) => foodId(mi) === matchedFood.id)
    : [];
  const showExistingNudge = targetValue === 'new' && existingForFood.length > 0;

  const onPickFile = (e) => {
    const picked = (e.target.files || [])[0];
    if (!picked) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(picked);
    setPreviewUrl(URL.createObjectURL(picked));
  };

  const onDishTextChange = (v) => {
    setDishName(v);
    // Editing the name re-opens matching — clear the old category.
    setMatchedFood(null);
  };

  // Picking a suggestion sets the CATEGORY only — it never overwrites the name.
  const pickSuggestion = (food) => {
    setMatchedFood({ id: food.id, name: food.name });
    setSuggestions([]);
  };

  const clearMatch = () => setMatchedFood(null);

  // Resolve the current selection into the contribution's target fields.
  const buildTarget = () => {
    if (targetValue === 'restaurant') {
      return { target_type: 'restaurant', target_id: restaurant.id };
    }
    if (targetValue === 'new') {
      // Always a NEW dish. Send the name AND the category when both are present
      // (#103) — the name becomes the new menu item's custom_name.
      const t = { target_type: 'new_menu_item', target_id: restaurant.id };
      const name = dishName.trim();
      if (name) t.proposed_name = name;
      if (matchedFood) t.matched_food_id = matchedFood.id;
      return t;
    }
    // 'mi:<id>'
    return { target_type: 'menu_item', target_id: Number(targetValue.slice(3)) };
  };

  const validationError = () => {
    if (!file) return 'Please choose a photo.';
    if (targetValue === 'new' && !matchedFood && dishName.trim().length < 2) {
      return 'Please name the dish (or pick a catalog match).';
    }
    return '';
  };

  const handleSubmit = async () => {
    const v = validationError();
    if (v) { setError(v); return; }
    if (!token) { setError('Please sign in to contribute a photo.'); return; }

    setBusy(true);
    setError('');
    try {
      const target = buildTarget();
      const jpeg = await prepJpeg(file);

      const presignRes = await fetch(`${API_BASE_URL}/contributions/presign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          target_type: target.target_type,
          target_id: target.target_id,
          content_type: 'image/jpeg',
          ext: 'jpg',
        }),
      });
      if (!presignRes.ok) throw new Error('Could not start the upload.');
      const { upload_url, storage_key } = await presignRes.json();

      const putRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/jpeg' },
        body: jpeg,
      });
      if (!putRes.ok) throw new Error('Upload to storage failed.');

      const recordRes = await fetch(`${API_BASE_URL}/contributions/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...target,
          storage_key,
          content_type: 'image/jpeg',
          caption: caption.trim() || null,
        }),
      });
      if (!recordRes.ok) {
        const detail = await recordRes.json().catch(() => ({}));
        throw new Error(detail.detail || 'Could not submit the photo.');
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={busy ? undefined : onHide} centered>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Add a photo</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {success ? (
          <Alert variant="success" className="mb-0">
            <strong>Thanks!</strong> Your photo was submitted and will appear once a moderator approves it.
          </Alert>
        ) : (
          <>
            {error && <Alert variant="danger">{error}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label>Photo of</Form.Label>
              <Form.Select
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                disabled={busy}
              >
                <option value="restaurant">{restaurant?.name || 'This restaurant'}</option>
                {menuItems.map((item) => (
                  <option key={item.id} value={`mi:${item.id}`}>
                    {item.custom_name || item.food?.name || `Menu item #${item.id}`}
                  </option>
                ))}
                <option value="new">Another dish…</option>
              </Form.Select>
            </Form.Group>

            {targetValue === 'new' && (
              <Form.Group className="mb-3">
                <Form.Label>Dish name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Chocolate Chip Muffin"
                  value={dishName}
                  onChange={(e) => onDishTextChange(e.target.value)}
                  disabled={busy}
                  autoComplete="off"
                />
                {matchedFood ? (
                  <div className="mt-2">
                    <span className="text-muted me-2 small">Catalog dish:</span>
                    <Badge bg="light" text="dark" className="border">
                      {matchedFood.name}{' '}
                      <span
                        role="button"
                        aria-label="Clear catalog dish link"
                        onClick={busy ? undefined : clearMatch}
                        style={{ cursor: busy ? 'default' : 'pointer' }}
                      >
                        ✕
                      </span>
                    </Badge>
                  </div>
                ) : (
                  <>
                    {suggestLoading && (
                      <Form.Text className="text-muted d-block">Searching…</Form.Text>
                    )}
                    {suggestions.length > 0 && (
                      <ListGroup className="mt-1">
                        {suggestions.map((food) => (
                          <ListGroup.Item
                            key={food.id}
                            action
                            onClick={() => pickSuggestion(food)}
                            disabled={busy}
                          >
                            {food.name}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                    <Form.Text className="text-muted d-block">
                      Optionally link a known dish from the catalog — your dish keeps its own name.
                    </Form.Text>
                  </>
                )}

                {showExistingNudge && (
                  <Alert variant="light" className="mt-3 mb-0 border small">
                    {restaurant?.name || 'This restaurant'} already lists{' '}
                    {existingForFood.length === 1 ? 'an item' : 'items'} under{' '}
                    <strong>{matchedFood.name}</strong>
                    {existingForFood.length <= 3 && (
                      <> ({existingForFood.map(miLabel).join(', ')})</>
                    )}
                    . This will be added as a <strong>new dish</strong>. If your photo is
                    actually of one you already list, pick it from <em>“Photo of”</em> above instead.
                  </Alert>
                )}
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Photo</Form.Label>
              <Form.Control
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onPickFile}
                disabled={busy}
              />
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="Selected preview"
                  className="mt-2 rounded"
                  style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'cover' }}
                />
              )}
            </Form.Group>

            <Form.Group className="mb-1">
              <Form.Label>Caption <span className="text-muted">(optional)</span></Form.Label>
              <Form.Control
                type="text"
                maxLength={200}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                disabled={busy}
              />
            </Form.Group>

            <Form.Text className="text-muted">
              Photos are reviewed before they appear publicly. Image metadata (including location) is removed before upload.
            </Form.Text>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {success ? (
          <Button variant="primary" onClick={onHide}>Done</Button>
        ) : (
          <>
            <Button variant="outline-secondary" onClick={onHide} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={busy}>
              {busy ? (<><Spinner as="span" size="sm" animation="border" className="me-2" />Sharing…</>) : 'Share publicly'}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ContributePhotoModal;
